import { Interceptor, Transport } from "@connectrpc/connect";
import { TransportProvider } from "@connectrpc/connect-query";
import { createConnectTransport } from "@connectrpc/connect-web";
import {
  QueryClient,
  QueryClientProvider as TanStackQueryClientProvider,
} from "@tanstack/react-query";
import { PropsWithChildren, useMemo } from "react";

import { firebaseAuth } from "../configs/firebase";
import { useSelector } from "../store";

const authorizationInterceptor: Interceptor = (fn) => {
  return async (req) => {
    await firebaseAuth.authStateReady();
    const token = await firebaseAuth.currentUser?.getIdToken();
    if (token) {
      req.header.set("authorization", `Bearer ${token}`);
    }
    return fn(req);
  };
};

const orgIdInterceptor =
  (organizationId: string): Interceptor =>
  (fn) => {
    return (req) => {
      if (organizationId) {
        req.header.set("Organization-Id", organizationId);
      }
      return fn(req);
    };
  };

const getTransport = (organizationId: string): Transport => {
  return createConnectTransport({
    baseUrl: "/",
    interceptors: [authorizationInterceptor, orgIdInterceptor(organizationId)],
  });
};

const getClient = (): QueryClient => {
  return new QueryClient();
};

export const QueryClientProvider = ({ children }: PropsWithChildren) => {
  const { organizationId } = useSelector((state) => state.current);
  const transport = useMemo(
    () => getTransport(organizationId),
    [organizationId],
  );
  const client = useMemo(() => getClient(), []);
  return (
    <TransportProvider transport={transport}>
      <TanStackQueryClientProvider client={client}>
        {children}
      </TanStackQueryClientProvider>
    </TransportProvider>
  );
};
